import {
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { ConfigurationService } from '@sc/data-access-configuration';
import { Car } from '@sc/data-access-models';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { startWith } from 'rxjs';
import {
  CheckboxComponent,
  InputTextComponent,
  LicensePlateNumberValidator,
} from '../components';

@Directive({
  selector: 'sc-input-text[formControlName][checkboxPlate]',
  standalone: true,
})
export class CheckboxPlateDirective implements OnInit {
  @Input() idCheckbox: string;
  @Input() classCheckbox: string;
  @Input() set checkboxText(value: string) {
    this._checkBoxText = value;
    if (this._checkBoxText.length) {
      this.updateCheckboxText();
    }
  }
  @Input() set currentCar(car: Car) {
    if (
      this.featureFlippingManager.isFeatureActivated(
        'checkbox-enable-plate-format'
      ) &&
      !!car
    ) {
      this.changeCheckboxValue(
        (car?.countryCode ===
          this.configurationService?.getDefaultLicensePlateCountry() &&
          !!car?.model &&
          !!car?.manufacturer) ||
          !car?.licensePlateNumber
      );
    }
  }

  @Output() checkboxUpdateEvent = new EventEmitter<boolean>();

  shouldApplyPlateValidation = true;
  componentRef: ComponentRef<CheckboxComponent>;
  _checkBoxText: string;

  constructor(
    private readonly it: InputTextComponent,
    private readonly element: ElementRef<HTMLElement>,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly configurationService: ConfigurationService,
    public featureFlippingManager: FeatureFlippingManagerService
  ) {}

  ngOnInit() {
    if (
      this.featureFlippingManager.isFeatureActivated(
        'checkbox-enable-plate-format'
      )
    ) {
      const text = document.createTextNode(this._checkBoxText);
      this.componentRef = this.viewContainerRef.createComponent(
        CheckboxComponent,
        { projectableNodes: [[text]] }
      );
      this.element.nativeElement.id = this.idCheckbox;
      this.element.nativeElement.classList.add(this.classCheckbox);

      this.componentRef?.instance?.changeValue.subscribe((checkboxValue) =>
        this.applyLicensePlateValidators(checkboxValue)
      );

      this.it.ngControl.statusChanges
        .pipe(startWith(this.it.ngControl.status))
        .subscribe((newStatus) => {
          if (newStatus === 'DISABLED') {
            this.componentRef?.instance?.setDisabledState(true);
          } else {
            this.componentRef?.instance?.setDisabledState(false);
          }
        });

      this.changeCheckboxValue(this.shouldApplyPlateValidation);
    }
  }

  //used to update the text of the checkbox, in order to handle the traductions
  updateCheckboxText() {
    if (this.componentRef) {
      const checkboxElement = this.componentRef.location.nativeElement;
      const labelElement = checkboxElement.querySelector('label');
      if (labelElement) {
        labelElement.textContent = this._checkBoxText;
      }
    }
  }

  changeCheckboxValue(bool: boolean) {
    if (this.componentRef) {
      this.shouldApplyPlateValidation = bool;
      this.componentRef.instance.value = this.shouldApplyPlateValidation;
    }
  }

  applyLicensePlateValidators(newValue?: boolean) {
    this.changeCheckboxValue(newValue ?? this.shouldApplyPlateValidation);

    const validators: ValidatorFn[] = [];

    validators.push(Validators.required);
    if (this.shouldApplyPlateValidation) {
      validators.push(
        LicensePlateNumberValidator.pattern(
          this.configurationService.getAllLicensePlateCountries()
        )
      );
    }

    this.it.ngControl.control.setValidators(validators);
    this.it.ngControl.control.updateValueAndValidity();
    if (newValue !== undefined && this.shouldApplyPlateValidation)
      this.it.ngControl.control.markAsDirty();
    this.checkboxUpdateEvent.emit(newValue);
  }
}
